import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Hook para monitorear la inactividad del usuario y gesti  // Configurar event listeners para detectar actividad (solo una vez)
  useEffect(() => {
    const handleActivity = (event) => {
      // Filtrar eventos que no son actividad real del usuario
      if (event.type === 'mousemove') {
        // Solo considerar mousemove si se mueve más de 10 píxeles
        if (!lastActivityRef.current.mouseX || !lastActivityRef.current.mouseY) {
          lastActivityRef.current.mouseX = event.clientX;
          lastActivityRef.current.mouseY = event.clientY;
          return;
        }
        
        const deltaX = Math.abs(event.clientX - lastActivityRef.current.mouseX);
        const deltaY = Math.abs(event.clientY - lastActivityRef.current.mouseY);
        
        if (deltaX < 10 && deltaY < 10) {
          return; // Movimiento insignificante, ignorar
        }
        
        lastActivityRef.current.mouseX = event.clientX;
        lastActivityRef.current.mouseY = event.clientY;
      }

      // Solo resetear si llevamos más de 3 segundos de inactividad para evitar spam
      const timeSinceLastActivity = Date.now() - lastActivityRef.current.lastReset;
      if (timeSinceLastActivity > 3000) {
        console.log(`👆 [SessionInactivity] Actividad REAL del usuario: ${event.type}`);
        lastActivityRef.current.lastReset = Date.now();
        resetInactivityTimer();
      } else {
        console.log(`🔇 [SessionInactivity] Actividad ignorada (spam): ${event.type} - ${timeSinceLastActivity}ms desde último reset`);
      }
    };tado de la sesión
 * @param {number} warningTime - Tiempo en minutos para mostrar advertencia (default: 4)
 * @param {number} logoutTime - Tiempo en minutos para cerrar sesión (default: 5)
 * @param {function} onLogout - Función a ejecutar cuando se cierre la sesión
 * @param {function} onWarning - Función a ejecutar cuando aparezca la advertencia
 */
export const useSessionInactivity = (
  warningTime = 9, // 9 minutos de actividad sin aviso
  logoutTime = 10, // 1 minuto de cuenta regresiva hasta cierre de sesión
  onLogout = () => {},
  onWarning = () => {}
) => {
  const [status, setStatus] = useState('active');
  const [timeLeft, setTimeLeft] = useState(0);
  const [showWarningModal, setShowWarningModal] = useState(false);
  
  const warningTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const lastActivityRef = useRef({ 
    lastReset: Date.now(),
    mouseX: null,
    mouseY: null
  });
  
  // Refs para mantener valores actuales sin causar re-renders
  const warningTimeRef = useRef(warningTime);
  const logoutTimeRef = useRef(logoutTime);
  const onLogoutRef = useRef(onLogout);
  const onWarningRef = useRef(onWarning);
  
  // Actualizar refs cuando cambien las props
  warningTimeRef.current = warningTime;
  logoutTimeRef.current = logoutTime;
  onLogoutRef.current = onLogout;
  onWarningRef.current = onWarning;

  // Lista de eventos que consideramos como actividad REAL del usuario
  const activityEvents = [
    'mousedown', 'mousemove', 'keypress', 'scroll', 
    'touchstart', 'click', 'keydown', 'wheel'
  ]; // Removido 'focus' y 'blur' que se disparan automáticamente

  // Función para resetear los timers de inactividad
  const resetInactivityTimer = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current.lastReset = now;
    setStatus('active');
    setShowWarningModal(false);
    setTimeLeft(0);

    const currentWarningTime = warningTimeRef.current;
    const currentLogoutTime = logoutTimeRef.current;

    console.log('🔄 [SessionInactivity] Timer reseteado:', new Date(now).toLocaleTimeString());
    console.log(`⏰ [SessionInactivity] Advertencia en: ${currentWarningTime} min, Logout en: ${currentLogoutTime} min`);

    // Limpiar timers existentes
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);

    const warningMs = currentWarningTime * 600 * 1000;
    const logoutMs = currentLogoutTime * 600 * 1000;
    const countdownSeconds = (logoutMs - warningMs) / 1000;

    console.log(`⚠️  [SessionInactivity] Configurando advertencia en ${warningMs}ms (${currentWarningTime} min)`);
    console.log(`🚪 [SessionInactivity] Configurando logout en ${logoutMs}ms (${currentLogoutTime} min)`);

    // Configurar timer para advertencia
    warningTimerRef.current = setTimeout(() => {
      console.log('⚠️  [SessionInactivity] ¡ADVERTENCIA ACTIVADA!');
      setStatus('warning');
      setShowWarningModal(true);
      setTimeLeft(countdownSeconds);
      onWarningRef.current();

      // Iniciar countdown
      countdownTimerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          const newTime = prev - 1;
          console.log(`⏳ [SessionInactivity] Countdown: ${newTime}s restantes`);
          if (newTime <= 1) {
            console.log('🔚 [SessionInactivity] Countdown terminado');
            clearInterval(countdownTimerRef.current);
            return 0;
          }
          return newTime;
        });
      }, 1000);

    }, warningMs);

    // Configurar timer para logout automático
    logoutTimerRef.current = setTimeout(() => {
      console.log('🚪 [SessionInactivity] ¡LOGOUT AUTOMÁTICO INICIADO!');
      console.log('🚪 [SessionInactivity] Cambiando estado a expired...');
      setStatus('expired');
      
      console.log('🚪 [SessionInactivity] Cerrando modal...');
      setShowWarningModal(false);
      
      console.log('🚪 [SessionInactivity] Ejecutando callback onLogout...');
      try {
        onLogoutRef.current();
        console.log('✅ [SessionInactivity] Callback onLogout ejecutado correctamente');
      } catch (error) {
        console.error('❌ [SessionInactivity] Error en callback onLogout:', error);
      }
    }, logoutMs);

  }, []); // Sin dependencias para evitar recreación constante

  // Función para extender la sesión manualmente
  const extendSession = useCallback(() => {
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  // Función para cerrar sesión manualmente
  const forceLogout = useCallback(() => {
    console.log('🚪 [SessionInactivity] ForceLogout ejecutado');
    
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    
    setStatus('expired');
    setShowWarningModal(false);
    
    console.log('🚪 [SessionInactivity] Ejecutando función onLogout...');
    onLogoutRef.current();
  }, []);

  // Configurar event listeners para detectar actividad (solo una vez)
  useEffect(() => {
    const handleActivity = (event) => {
      // Solo resetear si llevamos más de 1 segundo de inactividad para evitar spam
      const timeSinceLastActivity = Date.now() - lastActivityRef.current;
      if (timeSinceLastActivity > 1000) {
        console.log(`� [SessionInactivity] Actividad significativa: ${event.type}`);
        resetInactivityTimer();
      }
    };

    // Agregar listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, []); // Solo ejecutar una vez

  // Inicializar timer solo al montar
  useEffect(() => {
    console.log('🚀 [SessionInactivity] Inicializando hook...');
    resetInactivityTimer();

    // Cleanup al desmontar
    return () => {
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    };
  }, []); // Solo ejecutar una vez

  // Formatear tiempo restante para mostrar
  const formatTimeLeft = () => {
    const minutes = Math.floor(timeLeft / 60);
    const seconds = timeLeft % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Obtener configuración del estado actual
  const getStatusConfig = () => {
    switch (status) {
      case 'active':
        return {
          color: '#43e97b',
          text: 'Sistema activo',
          animation: 'pulse',
          bgColor: 'rgba(67, 233, 123, 0.1)',
          borderColor: 'rgba(67, 233, 123, 0.2)'
        };
      case 'warning':
        return {
          color: '#ffc409',
          text: `Sesión expira en ${formatTimeLeft()}`,
          animation: 'blink',
          bgColor: 'rgba(255, 196, 9, 0.1)',
          borderColor: 'rgba(255, 196, 9, 0.3)'
        };
      case 'expired':
        return {
          color: '#eb445a',
          text: 'Sesión expirada',
          animation: 'none',
          bgColor: 'rgba(235, 68, 90, 0.1)',
          borderColor: 'rgba(235, 68, 90, 0.3)'
        };
      default:
        return {
          color: '#43e97b',
          text: 'Sistema activo',
          animation: 'pulse',
          bgColor: 'rgba(67, 233, 123, 0.1)',
          borderColor: 'rgba(67, 233, 123, 0.2)'
        };
    }
  };

  return {
    status,
    timeLeft,
    formatTimeLeft: formatTimeLeft(),
    showWarningModal,
    setShowWarningModal,
    extendSession,
    forceLogout,
    resetInactivityTimer,
    statusConfig: getStatusConfig()
  };
};